import { useEffect, useRef } from 'react';
import simulationService from '@/services/simulationService';
import type { SimulationResponse } from '@/types/simulation';

const POLL_INTERVAL_MS = 8000;

/**
 * Refreshes any simulation with documentsReadiness === "Processing" every
 * POLL_INTERVAL_MS. Per-view hook: the caller owns the simulation list and
 * passes `onUpdate(updatedSim)` so the local state is the single source of
 * truth (consistent with the rest of the app — no global simulations store).
 *
 * Nothing is scheduled when there are no Processing ids — zero network cost
 * on idle views. The interval is torn down on unmount and re-created when
 * the observed list changes, so an id that transitions out of Processing
 * drops out of the polling set on the next tick.
 */
export function useDocumentProcessingPoller(
  simulations: SimulationResponse[] | undefined,
  onUpdate: (updated: SimulationResponse) => void,
): void {
  // Latest onUpdate kept in a ref so callers don't need to memoize it.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const processingIds = (simulations ?? [])
    .filter((s) => s.documentsReadiness === 'Processing')
    .map((s) => s.id);

  // Stable dependency key: empty string when nothing is being observed.
  const processingKey = processingIds.join(',');

  useEffect(() => {
    if (processingKey === '') return;
    const ids = processingKey.split(',');

    let cancelled = false;

    const tick = async () => {
      for (const id of ids) {
        try {
          const fresh = await simulationService.getById(id);
          if (cancelled) return;
          onUpdateRef.current(fresh);
        } catch {
          // Swallow: a transient failure (401, network blip) should not kill
          // the polling loop. The next tick will retry.
        }
      }
    };

    const handle = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [processingKey]);
}
